import React from "react";
import { Search } from "lucide-react";
import { sigMeta, fmt, cur, UP, DOWN } from "@/lib/format";
import { Input } from "@/components/ui/input";

const UNIVERSES = [
  { key: "all", label: "ALL" },
  { key: "nifty", label: "NIFTY 50" },
  { key: "sp500", label: "S&P 500" },
  { key: "etf", label: "ETF/IDX" },
];

export default function UniversePanel({ items, universe, setUniverse, q, setQ, selected, onSelect, loading }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-[4px] shadow-xs flex flex-col h-full min-h-0" data-testid="universe-panel">
      <div className="p-3.5 border-b border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-3">
          <span className="font-bold text-[13px] tracking-[0.08em] uppercase text-slate-700">Screener</span>
          <span className="font-mono text-[11px] text-slate-400 tabular-nums">{items.length} SYM</span>
        </div>
        <div className="flex gap-1 mb-3 p-0.5 bg-[#F1F5F9] rounded-[4px]">
          {UNIVERSES.map((u) => (
            <button
              key={u.key}
              onClick={() => setUniverse(u.key)}
              data-testid={`universe-tab-${u.key}`}
              className={`flex-1 font-semibold text-[11px] tracking-wide py-1.5 rounded-[3px] transition-colors duration-150 ${
                universe === u.key
                  ? "bg-[#121D28] text-white shadow-xs"
                  : "bg-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {u.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search ticker / name"
            data-testid="universe-search"
            className="h-9 pl-8 bg-[#F8FAFC] border-[#CBD5E1] text-slate-800 text-[14px] placeholder:text-slate-400 focus-visible:ring-blue-500/40"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0">
        {loading && items.length === 0 ? (
          <div className="p-4 font-mono text-[13px] text-slate-400">Scanning universe…</div>
        ) : items.length === 0 ? (
          <div className="p-4 font-mono text-[13px] text-slate-400">No symbols match.</div>
        ) : (
          items.map((it) => {
            const m = sigMeta(it.signal);
            const active = selected === it.ticker;
            const up = (it.change_pct ?? 0) >= 0;
            return (
              <button
                key={it.ticker}
                onClick={() => onSelect(it.ticker)}
                data-testid={`universe-row-${it.ticker}`}
                className={`w-full text-left px-3.5 py-2.5 border-b border-[#F1F5F9] flex items-center gap-2.5 transition-colors duration-150 border-l-[3px] ${
                  active ? "bg-[#EFF6FF] border-l-blue-600" : "border-l-transparent hover:bg-[#F8FAFC]"
                }`}
              >
                <span className="w-1 h-7 rounded-full shrink-0" style={{ background: m.color }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-[14px] text-slate-900 truncate">{it.ticker.replace(".NS", "").replace("^", "")}</span>
                    <span className="font-mono text-[10px] text-slate-400">{it.market}</span>
                    {it.asset_type && it.asset_type !== "STOCK" && (
                      <span className="text-[9px] font-semibold px-1 rounded-[2px] bg-[#EFF6FF] text-[#1E40AF] border border-[#BFDBFE]">{it.asset_type}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{it.name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-[13px] text-slate-800 tabular-nums font-semibold">{cur(it.market)}{fmt(it.price)}</div>
                  <div className="font-mono text-[12px] tabular-nums font-semibold" style={{ color: up ? UP : DOWN }}>
                    {up ? "+" : ""}{fmt(it.change_pct, 1)}%
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
